import { ChangeEvent, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, ProjectFile } from 'entities';

import { Button, Card, Column, LabeledInput, Modal, Row } from 'components/Blocks';
import { ArrowRightIcon, CloudUploadIcon, FileUpIcon } from 'components/Icons';
import { TruncateText } from 'components/TruncateText';
import { FileIconInput } from 'components/Uploads';

import { MIN_FILE_NAME_LENGTH } from './RenameFileButton';

import { useProjectProfile } from '../../context';

interface RenameTaskFileButtonProps {
  file: ProjectFile;
}

export function NewProjectFileVersion({ file }: RenameTaskFileButtonProps) {
  const { t } = useLang();

  const [newFile, setNewFile] = useState<File | null>(null);
  const [name, setName] = useState(file.name);

  const { updateProject } = useProjectProfile();

  const badName = name.trim().length < MIN_FILE_NAME_LENGTH;

  const newVersion = actions.projects.newProjectFileVersion.use({
    params: {
      name,
      fileId: file.id,
      projectId: file.projectId,
      file: newFile!,
    },

    block: !newFile || badName,

    onSuccess: (mutated) => {
      updateProject((old) => ({
        ...old,
        files: old.files.map((f) =>
          f.id === file.id ? { ...mutated, uploadedBy: file.uploadedBy } : f,
        ),
      }));

      setNewFile(null);
    },
  });

  const onFile = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const [file] = target.files ?? [];

    if (!file) return;

    setNewFile(file);
  };

  return (
    <>
      <FileIconInput
        onChange={onFile}
        multiple={false}
        tooltip={t('upload-new-version')}
        icon={<CloudUploadIcon size={16} />}
      />

      <Modal
        isOpen={!!newFile}
        close={() => setNewFile(null)}
        title={t('upload-new-version')}
        width="min(90vw, 560px)"
      >
        <Card flow="column" alignItems="center" className="text-primary" gap="3">
          <FileUpIcon size={36} />

          <span>{file.name}</span>

          <Row gap="6">
            <Column gap="1">
              <TruncateText text={file.filename} max={25} className="text-xs text-foreground" />

              <span className="text-xs italic text-foreground">
                {t('version')} {file.version}
              </span>
            </Column>

            <ArrowRightIcon size={20} className="self-start" />

            <Column gap="1">
              <TruncateText
                text={newFile?.name || ''}
                max={25}
                className="text-xs text-foreground"
              />

              <span className="text-xs font-medium italic text-primary-dark">
                {t('version')} {file.version + 1}
              </span>
            </Column>
          </Row>
        </Card>

        <LabeledInput value={name || file.name} onChangeDirect={setName} label={t('name')} />

        <Button disabled={badName} {...newVersion.buttonProps}>
          <CloudUploadIcon />
          {t('upload')}
        </Button>
      </Modal>
    </>
  );
}
